import cx from "classnames";
import { useContext, useLayoutEffect, useRef } from "react";

import { Box, Icon } from "metabase/ui";

import S from "./OnboardingStepper.module.css";
import { StepperContext } from "./OnboardingStepperContext";
import type { OnboardingStepperStepProps } from "./types";

const STEP_CONTENT_FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

export function OnboardingStepperStep({
  stepId,
  title,
  children,
  hideTitleOnActive = false,
  "data-testid": testId,
}: OnboardingStepperStepProps) {
  const {
    activeStep,
    completedSteps,
    lockedSteps,
    stepNumbers,
    stepRefs,
    setActiveStep,
  } = useContext(StepperContext);

  const ref = stepRefs[stepId];
  const titleRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const shouldMoveFocusRef = useRef(false);

  const isActive = activeStep === stepId;
  const isCompleted = completedSteps[stepId] ?? false;
  const isLocked = lockedSteps[stepId] ?? false;
  const stepNumber = stepNumbers[stepId] ?? 0;
  const isSelectable = !isActive && !isLocked;

  const shouldShowTitle = !isActive || !hideTitleOnActive;

  const activateStep = () => {
    if (!isSelectable) {
      return;
    }

    shouldMoveFocusRef.current = true;
    setActiveStep(stepId);
  };

  useLayoutEffect(() => {
    if (!isActive || !shouldMoveFocusRef.current) {
      return;
    }

    shouldMoveFocusRef.current = false;

    if (titleRef.current) {
      titleRef.current.focus();
      return;
    }

    const firstFocusable = contentRef.current?.querySelector<HTMLElement>(
      STEP_CONTENT_FOCUSABLE_SELECTOR,
    );
    firstFocusable?.focus();
  }, [isActive]);

  return (
    <Box
      component="li"
      ref={ref}
      className={S.StepRoot}
      aria-label={title}
      aria-current={isActive ? "step" : undefined}
      data-active={isActive}
      data-completed={isCompleted}
      data-locked={isLocked}
      data-testid={testId}
      onClick={isSelectable ? activateStep : undefined}
    >
      <div className={S.StepNumber} data-completed={isCompleted}>
        {isCompleted ? (
          <Icon name="check" className={S.StepNumberIcon} />
        ) : (
          <span className={S.StepNumberText}>{stepNumber}</span>
        )}
      </div>

      {isSelectable ? (
        <button type="button" className={S.StepActivateButton}>
          <span
            className={cx(
              S.StepHeader,
              shouldShowTitle && S.StepHeaderWithTitle,
            )}
          >
            <span className={S.StepTitle} data-completed={isCompleted}>
              {title}
            </span>
          </span>
        </button>
      ) : (
        <div
          className={cx(S.StepHeader, shouldShowTitle && S.StepHeaderWithTitle)}
        >
          {shouldShowTitle && (
            <div
              ref={titleRef}
              className={S.StepTitle}
              tabIndex={-1}
              data-completed={isCompleted}
            >
              {title}
            </div>
          )}

          {isLocked && !isActive && (
            <Icon name="lock" className={S.StepLockIcon} aria-label="Locked" />
          )}
        </div>
      )}

      {isActive && (
        <div
          ref={contentRef}
          className={cx(shouldShowTitle && S.StepContentWithTitle)}
        >
          {children}
        </div>
      )}
    </Box>
  );
}
